"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.ResponseAudioPlaybackHelper = void 0;
class ResponseAudioPlaybackHelper {
    /**
     * Handle audio response from the conversation service
     */
    static handleAudioResponse(component, response) {
        var _a;
        console.log('🔊 Audio response received:', {
            sessionId: response.sessionId,
            responseLength: (_a = response.response) === null || _a === void 0 ? void 0 : _a.length,
            hasAudio: !!response.audio
        });
        try {
            // Note: Backend already saves the assistant message, so we don't add it here to avoid duplicates
            // Clear thinking state since agent is responding
            component.isThinking = false;
            component.isLoading = false;
            // Trigger audio playback if we have audio data
            if (response.audio) {
                component.conversationAgentService.playAudioFromBase64(response.audio)
                    .catch(error => {
                    console.error('❌ [AudioPlayback] Audio playback failed - SKIPPING TO LISTENING:', error);
                    this.skipToListening(component);
                });
            }
            else {
                // No audio - go straight to listening
                console.log('🎤 [AudioPlayback] No audio provided - going straight to listening');
                this.skipToListening(component);
            }
            // Don't set userTurn yet - this will be handled when speaking ends
        }
        catch (error) {
            console.error('❌ [AudioPlayback] Error during audio processing - SKIPPING TO LISTENING:', error);
            console.log('🎤 [AudioPlayback] EMERGENCY RECOVERY: Skipping audio, going straight to listening');
            this.skipToListening(component);
        }
    }
    /**
     * Handle text response when TTS fails (fallback)
     */
    static handleTextResponse(component, response) {
        var _a;
        console.log('📄 Text response received (TTS fallback):', {
            sessionId: response.sessionId,
            responseLength: (_a = response.response) === null || _a === void 0 ? void 0 : _a.length
        });
        // Note: Backend already saves the assistant message, so we don't add it here to avoid duplicates
        component.isThinking = false;
        component.isLoading = false;
        // Back to user turn since no audio was generated
        component.userTurn = true;
    }
    /**
     * Handle general conversation response
     */
    static handleConversationResponse(component, response) {
        console.log('💬 Conversation response received:', response);
        // Note: Backend already saves the assistant message, so we don't add it here to avoid duplicates
        // Clear thinking state since agent is responding
        component.isThinking = false;
        component.isLoading = false;
        // Audio playback is now handled automatically by the refactored service
        // Speaking status is handled by the callback system
        // userTurn will be set when speaking callback indicates agent stopped speaking
    }
    /**
     * Set volume for audio playback
     */
    static setVolume(component, volume) {
        component.volume = Math.max(0, Math.min(1, volume));
        component.conversationAgentService.setVolume(component.volume * 100);
    }
    /**
     * Stop all audio playback
     */
    static stopPlayback(component) {
        component.conversationAgentService.stopPlayback();
    }
    /**
     * Clear all messages from the conversation
     */
    static clearMessages(component) {
        component.messages = [];
        component.userTranscriptions = [];
    }
    /**
     * Handle conversation end
     */
    static handleConversationEnd(component, response) {
        // The actual endConversation logic should be handled by the component
        // This is just for cleanup specific to audio playback
        this.stopPlayback(component);
    }
    /**
     * Handle conversation error
     */
    static handleConversationError(component, response) {
        var _a;
        console.error('Conversation error:', response);
        component.errorMessage = ((_a = response.data) === null || _a === void 0 ? void 0 : _a.error) || 'conversation.error'.t();
        component.updateIsReady();
        // Stop any ongoing playback
        this.stopPlayback(component);
        component.isLoading = false;
    }
    /**
     * Get current playback status
     */
    static getPlaybackStatus(component) {
        return {
            isSpeaking: component.isSpeaking,
            volume: component.volume,
            hasActiveAudio: component.isSpeaking || component.isVoiceStreaming
        };
    }
    /**
     * Handle speaking state change
     */
    static async handleSpeakingChanged(component, isSpeaking) {
        try {
            component.isSpeaking = isSpeaking;
            if (component.isSpeaking) {
                // Agent started speaking - start STT session for interruptions only
                component.isThinking = false;
                component.userTurn = false;
                console.log('🔊 [AudioPlayback] STAGE 2: Agent started speaking - enabling microphone for interruptions');
                console.log('🎤 [AudioPlayback] MICROPHONE ENABLED: Starting STT for interruptions during agent speech');
                if (component.isConversationActive && component.currentSessionId) {
                    try {
                        // Start fresh STT session for interruptions
                        await component.conversationAgentService.startAudioStreaming(component.currentSessionId, {
                            sampleRate: 16000,
                            channels: 1,
                            bufferSize: 2048,
                            chunkDuration: 100,
                            enableVAD: true
                        });
                        component.isRecording = true;
                        console.log('✅ [AudioPlayback] STT session started - microphone active for interruptions');
                        console.log('🎤 [AudioPlayback] MICROPHONE STATUS: Now listening for user interruptions');
                    }
                    catch (error) {
                        console.error('❌ [AudioPlayback] Failed to start STT session for interruptions:', error);
                    }
                }
            }
            else {
                // Agent stopped speaking - RESTART FULL SESSION FOR FRESH CONNECTION
                component.userTurn = true;
                component.isThinking = false;
                // Clear any lingering transcription from previous interactions
                component.currentTranscription = '';
                component.partialTranscription = false;
                console.log('🎯 [AudioPlayback] STAGE 3: Agent finished speaking - preparing session restart');
                console.log('🛑 [AudioPlayback] MICROPHONE DISABLED: Agent speech complete');
                console.log('🔍 [AudioPlayback] Will restart with conversation ID:', component.currentConversationId);
                if (component.isConversationActive && component.currentSessionId) {
                    // Check if we have conversation ID for restart
                    if (!component.currentConversationId) {
                        console.error('❌ [AudioPlayback] No conversation ID stored - cannot restart session');
                        console.error('❌ [AudioPlayback] Current sessionId:', component.currentSessionId);
                        console.error('❌ [AudioPlayback] Component state:', {
                            isConversationActive: component.isConversationActive,
                            currentSessionId: component.currentSessionId,
                            currentConversationId: component.currentConversationId
                        });
                        return;
                    }
                    // FULL SESSION RESTART after every agent response
                    setTimeout(async () => {
                        try {
                            console.log('🔄 [AudioPlayback] STAGE 4: RESTARTING ENTIRE SESSION AFTER AGENT RESPONSE');
                            console.log('🎤 [AudioPlayback] MICROPHONE STATUS: Preparing to re-enable for next user input');
                            console.log('🔍 [AudioPlayback] Using conversation ID:', component.currentConversationId);
                            // 1. End current session
                            await component.conversationAgentService.endConversationAgent(component.currentSessionId, 'agent_finished');
                            // 2. Clear session state
                            component.currentSessionId = null;
                            component.isConversationActive = false;
                            component.isRecording = false;
                            // 3. Start fresh session with stored conversation ID
                            await component.startConversation({
                                avatar: component.avatar || '',
                                model: component.model,
                                hasValidSelection: true,
                                voice: component.chatSettings.voice,
                                conversationId: component.currentConversationId
                            });
                            console.log('✅ [AudioPlayback] Full session restarted after agent response');
                            console.log('🎤 [AudioPlayback] MICROPHONE ENABLED: Ready for next user input after session restart');
                        }
                        catch (error) {
                            console.error('❌ [AudioPlayback] Failed to restart session after agent response:', error);
                        }
                    }, 100); // Immediate restart
                }
            }
        }
        catch (error) {
            console.error('❌ [AudioPlayback] Error in speaking state change - EMERGENCY RECOVERY:', error);
            console.log('🎤 [AudioPlayback] EMERGENCY: Skipping to listening due to speaking state error');
            // Force listening state
            component.isSpeaking = false;
            component.isThinking = false;
            component.userTurn = true;
            // Emergency restart
            setTimeout(async () => {
                try {
                    await component.startConversation({
                        avatar: component.avatar || '',
                        model: component.model,
                        hasValidSelection: true,
                        voice: component.chatSettings.voice,
                        conversationId: component.currentConversationId
                    });
                    console.log('✅ [AudioPlayback] Emergency recovery from speaking error complete');
                }
                catch (recoveryError) {
                    console.error('❌ [AudioPlayback] Speaking error recovery failed:', recoveryError);
                }
            }, 500);
        }
    }
    /**
     * Skip audio playback and go straight to listening state
     */
    static skipToListening(component) {
        console.log('🎤 [AudioPlayback] SKIP TO LISTENING: Bypassing audio for immediate voice input');
        // Set component to listening state
        component.isSpeaking = false;
        component.isThinking = false;
        component.userTurn = true;
        // Immediately restart session for next user input
        setTimeout(async () => {
            var _a;
            try {
                console.log('🔄 [AudioPlayback] EMERGENCY SESSION RESTART after audio skip');
                // End current session if exists
                if (component.currentSessionId) {
                    await component.conversationAgentService.endConversationAgent(component.currentSessionId, 'audio_skip');
                }
                // Clear session state
                component.currentSessionId = null;
                component.isConversationActive = false;
                component.isRecording = false;
                // Start fresh session immediately
                await component.startConversation({
                    avatar: component.avatar || '',
                    model: component.model,
                    hasValidSelection: true,
                    voice: ((_a = component.chatSettings) === null || _a === void 0 ? void 0 : _a.voice) || null,
                    conversationId: component.currentConversationId
                });
                console.log('✅ [AudioPlayback] Emergency recovery complete - ready for user input');
                console.log('🎤 [AudioPlayback] MICROPHONE ENABLED: Ready after audio skip');
            }
            catch (recoveryError) {
                console.error('❌ [AudioPlayback] Emergency recovery after audio skip failed:', recoveryError);
            }
        }, 300); // Quick recovery
    }
}
exports.ResponseAudioPlaybackHelper = ResponseAudioPlaybackHelper;
